import CollectionLayer from '../Viewer/Map/CollectionLayer.jsx';
import Map from '@apps-common/ui-maps/Map.jsx';
import Overlay from '@planet/maps/Overlay.js';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReservationsOverlay from './ReservationsOverlay.jsx';
import {array, func, object, oneOfType} from 'prop-types';
import {useAuth} from '@apps-common/auth-react';

const QuotaManagerMap = ({
  collection,
  selectedFeatureRefs = [],
  zoomFeature,
  onZoomToFeature,
  selectedStyle,
  baseStyle,
  products,
  reservations,
}) => {
  const mapRef = useRef();
  const {user} = useAuth();

  const [popupElement, setPopupElement] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [overlayProducts, setSelectedOverlayProducts] = useState([]);

  const collectionExtent = useMemo(
    () => collection.extent.spatial.bbox[0],
    [collection]
  );

  const onClosePopUp = useCallback(() => setPopupPosition(null), []);

  const zoomToExtent = useCallback(extent => {
    const mapView = mapRef.current.getView();

    mapView.fit(extent, {
      padding: [48, 48, 48, 48],
    });
  }, []);

  const onLayerClick = (event, features) => {
    const reservationProducts = features
      .map(f => {
        const featureHashId = f.properties_['pl:ref'].split('/').pop();
        const featureReservations = reservations
          .filter(r => r.aoi_ref.endsWith(featureHashId))
          .map(r => r.product_access_id || r.product_id);

        const reservationProducts = products
          .filter(p => featureReservations.includes(p.id))
          .map(p => ({id: p.id, title: p.title}));
        return reservationProducts;
      })
      .flat();

    if (!features.length || !reservationProducts.length) {
      setPopupPosition(null);
      return;
    }

    setSelectedOverlayProducts(reservationProducts);
    setPopupPosition(event.coordinate);
  };

  useEffect(() => {
    if (mapRef?.current) {
      zoomToExtent(collectionExtent);
    }
  }, [mapRef, zoomToExtent, collectionExtent]);

  useEffect(() => {
    if (zoomFeature) {
      zoomToExtent(zoomFeature.bbox);
      if (onZoomToFeature) {
        onZoomToFeature();
      }
    }
  }, [zoomToExtent, zoomFeature, onZoomToFeature]);

  return (
    <>
      <Map
        mapRef={mapRef}
        onZoomToFeatures={() => zoomToExtent(collectionExtent)}
      >
        <CollectionLayer
          collectionId={collection.id}
          token={user.accessToken}
          selectedFeatures={selectedFeatureRefs}
          selectedStyle={selectedStyle}
          style={baseStyle}
          onClick={onLayerClick}
        />
        <Overlay
          element={popupElement}
          position={popupPosition}
          positioning="bottom-right"
        />
      </Map>
      <ReservationsOverlay
        innerRef={setPopupElement}
        onClose={onClosePopUp}
        reservations={overlayProducts}
      />
    </>
  );
};

QuotaManagerMap.propTypes = {
  collection: object,
  selectedFeatureRefs: array,
  zoomFeature: object,
  onZoomToFeature: func,
  selectedStyle: oneOfType([array, func, object]),
  baseStyle: oneOfType([array, func, object]),
  reservations: array,
  products: array,
};

export default QuotaManagerMap;
